/**
 * E-FE.10 — Sentry shape-mismatch / runtime capture.
 *
 * Работает в трёх режимах:
 *  - global Sentry set on window (via Sentry SDK) → прокинет event.
 *  - VITE_SENTRY_DSN конфигурирован → lazy-загрузка @sentry/react (optional).
 *  - dev без DSN → `console.warn` с shape-diff (pretty-print issue[]).
 *
 * Важно: НЕ отправляет rawData в prod (PII-risk). Только schema + zod-issue codes.
 */

type SentryLike = {
  captureException: (e: unknown, ctx?: Record<string, unknown>) => void;
};

function getSentry(): SentryLike | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { Sentry?: SentryLike };
  return w.Sentry ?? null;
}

/**
 * С-I3 Р.3 (TCA-B8) — образец ответа в Sentry без секретов. Ответ выпуска приглашения несёт
 * `accept_url` со ссылкой (В-50: «ссылку видит только администратор, один раз»), и расхождение
 * схемы отправило бы её во внешний сервис. Ключи со ссылкой и токенами скрываются по имени, ссылки
 * с `token=` — в любом поле.
 */
const SECRET_KEY = /^(accept_url|.*token.*|password|secret|sealed)$/i;
const TOKEN_IN_URL = /([?&](?:token|access_token|refresh_token)=)[^"&\s]+/gi;

export function previewWithoutSecrets(rawData: unknown, limit = 800): string {
  const json = JSON.stringify(rawData, (key, value) =>
    key && SECRET_KEY.test(key) ? '[скрыто]' : value,
  );
  return (json ?? '').replace(TOKEN_IN_URL, '$1[скрыто]').slice(0, limit);
}

export function captureShapeMismatch(
  schema: string,
  error: unknown,
  rawData: unknown | undefined,
): void {
  const sentry = getSentry();
  if (sentry) {
    sentry.captureException(error, {
      tags: { kind: 'shape_mismatch', schema },
      extra: {
        rawData_preview: rawData === undefined ? undefined : previewWithoutSecrets(rawData),
      },
    });
    return;
  }
  if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
    // eslint-disable-next-line no-console -- dev-only diagnostic
    console.warn(`[shape-mismatch] schema=${schema}`, error);
  }
}

export function captureRuntimeError(error: unknown, context: Record<string, unknown> = {}): void {
  const sentry = getSentry();
  if (sentry) {
    sentry.captureException(error, { tags: { kind: 'runtime' }, extra: context });
    return;
  }
  if (typeof import.meta !== 'undefined' && import.meta.env?.DEV) {
    // eslint-disable-next-line no-console -- dev-only diagnostic
    console.warn('[runtime-error]', error, context);
  }
}
